import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './ImageProcessingPage.css';
import ParameterPanel from '../components/parameters/ParameterPanel';
import { useTierParameters } from '../hooks/useTierParameters';
import pipelineService from '../services/pipelineService';
import { parameterService } from '../services/parameterService';
import MeshPreview from '../components/preview/MeshPreview';
import SecureImage from '../components/preview/SecureImage';
import DownloadSection from '../components/download/DownloadSection';
import { debugLog } from '../utils/logger';
import { useLocation, useNavigate } from 'react-router-dom';

const STEP_NAMES = new Set(['background_removal', 'depth_generation', 'pointcloud_generation', 'mesh_generation']);
const STATUS_POLL_ERROR_MESSAGE = 'Unable to reach pipeline status. Retrying…';
const STATUS_POLL_VISIBLE_INTERVAL_MS = 3500;
const STATUS_POLL_HIDDEN_INTERVAL_MS = 12000;
const STATUS_POLL_RESUME_MIN_DELAY_MS = 5000;

const STEP_LABELS = {
  background_removal: 'Background Removal',
  depth_generation: 'Depth Generation',
  pointcloud_generation: 'Point Cloud',
  mesh_generation: 'Mesh Generation',
  completed: 'Completed',
  processing: 'Processing',
  pending: 'Pending',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const IMAGE_PATTERN = /(\.png|\.jpe?g|\.gif|\.webp|\.bmp|\.tiff?)$/i;
const TEXTURE_NAME_PATTERN = /(^|[/_.-])(texture|albedo|diffuse)([/_.-]|$)/i;
const OBJ_PATTERN = /\.obj$/i;
const MASKING_DISABLED_WARNING =
  'Foreground mask requires background removal or an uploaded alpha mask. Masking has been disabled for this session.';

const PROCESSING_STEPS = [
  { id: 'upload', title: 'Upload', description: 'Add your source image.' },
  { id: 'configure', title: 'Configure', description: 'Choose presets or tweak settings.' },
  { id: 'review', title: 'Review', description: 'Resolve warnings and prepare the job.' },
  { id: 'process', title: 'Process', description: 'Run the pipeline and monitor progress.' },
];

const PROCESSING_PRESETS = [
  {
    id: 'fast',
    label: 'Fast Preview',
    description: 'Quick turnaround for rough previews.',
    overrides: {
      'depth_generation.min_depth': 0.2,
      'depth_generation.max_depth': 1.5,
      'pointcloud_generation.max_points': 60000,
      'mesh_generation.algorithms.grid_from_depth.target_face_count': 60000,
      'mesh_generation.algorithms.grid_from_depth.max_depth_difference': 10,
    },
  },
  {
    id: 'standard',
    label: 'Standard',
    description: 'Balanced defaults for most subjects.',
    overrides: {
      'depth_generation.min_depth': 0.3,
      'depth_generation.max_depth': 2.5,
      'pointcloud_generation.max_points': 120000,
      'mesh_generation.algorithms.grid_from_depth.target_face_count': 90000,
      'mesh_generation.algorithms.grid_from_depth.max_depth_difference': 18,
    },
  },
  {
    id: 'detail',
    label: 'High Detail',
    description: 'Prioritise fidelity for production assets.',
    overrides: {
      'depth_generation.min_depth': 0.2,
      'depth_generation.max_depth': 4,
      'pointcloud_generation.max_points': 250000,
      'mesh_generation.algorithms.grid_from_depth.target_face_count': 130000,
      'mesh_generation.algorithms.grid_from_depth.max_depth_difference': 28,
    },
  },
];

const PRESET_TRACKING_KEYS = new Set(
  PROCESSING_PRESETS.reduce((all, preset) => {
    return all.concat(Object.keys(preset.overrides));
  }, [])
);


const prettifyStepName = (stepType) => {
  if (!stepType) {
    return '';
  }
  if (STEP_LABELS[stepType]) {
    return STEP_LABELS[stepType];
  }
  return stepType
    .split('_')
    .map((segment) => (segment ? segment[0].toUpperCase() + segment.slice(1) : segment))
    .join(' ');
};

const mergeFileEntries = (entry) => {
  if (!entry) {
    return [];
  }
  const consolidated = Array.isArray(entry.consolidated_files) ? entry.consolidated_files : [];
  const fallback = Array.isArray(entry.fallback_files) ? entry.fallback_files : [];
  return [...consolidated, ...fallback];
};

const dedupeFiles = (primary = [], secondary = []) => {
  const seen = new Set();
  const result = [];

  primary.forEach((file) => {
    if (!file || !file.filename || seen.has(file.filename)) {
      return;
    }
    seen.add(file.filename);
    result.push(file);
  });

  secondary.forEach((file) => {
    if (!file) {
      return;
    }
    const filename = typeof file === 'string' ? file : file.filename;
    if (!filename || seen.has(filename)) {
      return;
    }
    seen.add(filename);
    if (typeof file === 'string') {
      result.push({ filename });
    } else {
      result.push(file);
    }
  });

  return result;
};

const collectStepFilesFromSessionFiles = (sessionFiles, stepType) => {
  if (!sessionFiles?.files_by_step) {
    return { stepType, files: [] };
  }
  const entry = Object.values(sessionFiles.files_by_step).find((item) => item?.step_type === stepType);
  if (!entry) {
    return { stepType, files: [] };
  }
  const consolidated = Array.isArray(entry.consolidated_files) ? entry.consolidated_files : [];
  const fallback = Array.isArray(entry.fallback_files) ? entry.fallback_files : [];
  return {
    stepType: entry.step_type || stepType,
    files: [...consolidated, ...fallback].filter((file) => Boolean(file?.filename)),
  };
};

const pickSourceOriginalFile = (sessionFiles) => {
  const { stepType, files } = collectStepFilesFromSessionFiles(sessionFiles, 'background_removal');
  const imageFiles = files.filter((file) => isImageFile(file.filename));
  const nonTextureImages = imageFiles.filter((file) => !isTextureFile(file.filename));
  const candidates = nonTextureImages;
  const prioritized = candidates.find((file) => String(file.filename).toLowerCase().includes('original'));
  const selected = prioritized || candidates[0];
  return selected ? { stepType, filename: selected.filename, file: selected } : null;
};

const pickSourceTextureFile = (sessionFiles) => {
  const { stepType, files } = collectStepFilesFromSessionFiles(sessionFiles, 'mesh_generation');
  const imageFiles = files.filter((file) => isImageFile(file.filename));
  const textureFiles = imageFiles.filter((file) => isTextureFile(file.filename));
  const prioritized = textureFiles.find((file) => String(file.filename).toLowerCase().includes('texture'));
  const selected = prioritized || textureFiles[0] || imageFiles[0];
  return selected ? { stepType, filename: selected.filename, file: selected } : null;
};

const flattenStepParametersToValues = (stepParameters) => {
  if (!stepParameters || typeof stepParameters !== 'object') {
    return {};
  }

  const flattened = {};

  const visit = (prefix, value) => {
    if (value === null || value === undefined) {
      return;
    }
    if (Array.isArray(value)) {
      flattened[prefix] = value;
      return;
    }
    if (typeof value !== 'object') {
      flattened[prefix] = value;
      return;
    }
    Object.entries(value).forEach(([key, nestedValue]) => {
      const nextPrefix = prefix ? `${prefix}.${key}` : key;
      visit(nextPrefix, nestedValue);
    });
  };

  Object.entries(stepParameters).forEach(([stepKey, payload]) => {
    if (!payload || typeof payload !== 'object') {
      return;
    }
    Object.entries(payload).forEach(([paramKey, paramValue]) => {
      visit(`${stepKey}.${paramKey}`, paramValue);
    });
  });

  return flattened;
};

const isImageFile = (filename) => IMAGE_PATTERN.test(filename || '');
const isTextureFile = (filename) => {
  if (!isImageFile(filename)) {
    return false;
  }
  return TEXTURE_NAME_PATTERN.test(String(filename || '').toLowerCase());
};
const isObjFile = (filename) => {
  const result = OBJ_PATTERN.test(filename || '');
  debugLog('isObjFile check:', { filename, result });
  return result;
};

const getVisibilityAwareDelay = (visibleDelayMs, hiddenDelayMs) => {
  if (typeof document === 'undefined') {
    return visibleDelayMs;
  }
  return document.visibilityState === 'hidden' ? hiddenDelayMs : visibleDelayMs;
};

const isEmptyValue = (value) => value === '' || value === undefined || value === null;

const normalizeBoolean = (val) => {
  if (typeof val === 'boolean') {
    return val;
  }
  if (typeof val === 'string') {
    const lowered = val.toLowerCase();
    if (lowered === 'true') {
      return true;
    }
    if (lowered === 'false') {
      return false;
    }
  }
  return Boolean(val);
};

const fileMayContainAlpha = (file) => {
  const type = String(file?.type || '').toLowerCase();
  const name = String(file?.name || '').toLowerCase();
  if (type.includes('png') || type.includes('webp') || type.includes('tiff') || type.includes('gif')) {
    return true;
  }
  return ['.png', '.webp', '.tif', '.tiff', '.gif'].some((ext) => name.endsWith(ext));
};

const detectAlphaMaskFromFile = async (file) => {
  if (!file || typeof document === 'undefined' || !fileMayContainAlpha(file)) {
    return false;
  }

  const probeUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise((resolve, reject) => {
      const nextImage = new Image();
      nextImage.onload = () => resolve(nextImage);
      nextImage.onerror = () => reject(new Error('Unable to decode image file'));
      nextImage.src = probeUrl;
    });

    const srcWidth = Math.max(1, image.naturalWidth || image.width || 1);
    const srcHeight = Math.max(1, image.naturalHeight || image.height || 1);
    const maxProbeSize = 512;
    const scale = Math.min(1, maxProbeSize / Math.max(srcWidth, srcHeight));
    const probeWidth = Math.max(1, Math.round(srcWidth * scale));
    const probeHeight = Math.max(1, Math.round(srcHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = probeWidth;
    canvas.height = probeHeight;
    const context = canvas.getContext('2d');
    if (!context) {
      return false;
    }

    context.clearRect(0, 0, probeWidth, probeHeight);
    context.drawImage(image, 0, 0, probeWidth, probeHeight);
    const pixels = context.getImageData(0, 0, probeWidth, probeHeight).data;

    for (let index = 3; index < pixels.length; index += 4) {
      if (pixels[index] < 255) {
        return true;
      }
    }
    return false;
  } catch {
    return false;
  } finally {
    URL.revokeObjectURL(probeUrl);
  }
};

const buildStepParameters = (values, parameterData) => {
  if (!values && !parameterData) {
    return {};
  }

  const coerceParamValue = (fullKey, lastSegment, val) => {
    if (val == null || val === '') return val;
    if (typeof val !== 'number') return val;
    // Enforce integer for specific integer-only params
    if (lastSegment === 'target_face_count') {
      return Math.round(val);
    }
    return val;
  };

  // First, materialize parameters from the current UI values.
  const result = (values ? Object.entries(values) : []).reduce((accumulator, [key, value]) => {
    if (isEmptyValue(value)) {
      return accumulator;
    }

    const segments = key.split('.');
    const stepIndex = segments.findIndex((segment) => STEP_NAMES.has(segment));
    if (stepIndex === -1) {
      return accumulator;
    }

    const stepName = segments[stepIndex];
    const paramPath = segments.slice(stepIndex + 1);
    if (paramPath.length === 0) {
      return accumulator;
    }

    if (!accumulator[stepName]) {
      accumulator[stepName] = {};
    }

    let pointer = accumulator[stepName];
    for (let index = 0; index < paramPath.length; index += 1) {
      const segment = paramPath[index];
      const isLast = index === paramPath.length - 1;
      if (isLast) {
        const nextValue = segment === '__enabled'
          ? normalizeBoolean(value)
          : coerceParamValue(key, segment, value);
        pointer[segment] = nextValue;
      } else {
        if (typeof pointer[segment] !== 'object' || pointer[segment] === null) {
          pointer[segment] = {};
        }
        pointer = pointer[segment];
      }
    }

    return accumulator;
  }, {});

  // Then, ensure each known step has a consistent __enabled flag derived from tier limits
  // when the UI did not provide one (for example when a step is hidden for this tier).
  const sections = parameterData?.raw?.sections || {};
  STEP_NAMES.forEach((stepName) => {
    const section = sections[stepName];
    if (!section || typeof section !== 'object') {
      return;
    }

    const stepEntry = result[stepName] || {};
    if (Object.prototype.hasOwnProperty.call(stepEntry, '__enabled')) {
      // UI already decided; keep it.
      result[stepName] = stepEntry;
      return;
    }

    let enabled;
    if (section.available === false) {
      enabled = false;
    } else if (typeof section.default_enabled === 'boolean') {
      enabled = section.default_enabled;
    } else {
      enabled = true;
    }

    if (!result[stepName]) {
      result[stepName] = {};
    }
    result[stepName].__enabled = enabled;
  });

  return result;
};

const resolveBackgroundRemoval = (values, parameterData) => {
  const section = parameterData?.raw?.sections?.background_removal;

  // If the tier marks background removal as unavailable, always disable it.
  if (section && section.available === false) {
    return false;
  }

  if (values) {
    const entry = Object.entries(values).find(
      ([key]) => key.includes('background_removal') && key.endsWith('.__enabled')
    );
    if (entry) {
      const rawValue = entry[1];
      if (typeof rawValue === 'string') {
        return rawValue.toLowerCase() === 'true';
      }
      return Boolean(rawValue);
    }
  }

  // No explicit toggle value; respect tier default when known.
  if (section && typeof section.default_enabled === 'boolean') {
    return section.default_enabled;
  }

  // Historical default: keep background removal enabled when in doubt.
  return true;
};

const findDefaultAlgorithm = (groups) => {
  if (!Array.isArray(groups)) {
    return null;
  }

  const rootMeshGroup = groups.find((group) => group.id?.includes('mesh_generation') && group.meta?.algorithm);
  if (rootMeshGroup?.meta?.algorithm?.default) {
    return rootMeshGroup.meta.algorithm.default;
  }

  const firstAlgorithmGroup = groups.find((group) => group.kind === 'algorithm' && group.meta?.algorithmId);
  return firstAlgorithmGroup?.meta?.algorithmId ?? null;
};

const resolveAlgorithm = (values, groups) => {
  if (values) {
    const entry = Object.entries(values).find(
      ([key]) => key.includes('mesh_generation') && key.endsWith('.algorithm')
    );
    if (entry && typeof entry[1] === 'string' && entry[1].trim()) {
      return entry[1];
    }
  }

  return findDefaultAlgorithm(groups) || null;
};

const ImageProcessingPage = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | ready | processing | completed | failed
  const [error, setError] = useState('');
  const [resizeInfo, setResizeInfo] = useState(null);
  const [parameterValues, setParameterValues] = useState({});
  const [sessionId, setSessionId] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [sessionFiles, setSessionFiles] = useState(null);
  const [sessionFilesError, setSessionFilesError] = useState('');
  const [isPreparing, setIsPreparing] = useState(false);
  const [isFetchingFiles, setIsFetchingFiles] = useState(false);
  const [textureFile, setTextureFile] = useState(null);
  const [maskFile, setMaskFile] = useState(null);
  const [editorScenePayload, setEditorScenePayload] = useState(null);
  const [textureSettings, setTextureSettings] = useState(null);
  const [activePreviewTab, setActivePreviewTab] = useState('mesh');
  const [activeProcessingTab, setActiveProcessingTab] = useState('status');
  const [activePreset, setActivePreset] = useState('custom');
  const [userPresets, setUserPresets] = useState([]);
  const [presetSaving, setPresetSaving] = useState(false);
  const [presetError, setPresetError] = useState('');
  const [reworkSourceSessionId, setReworkSourceSessionId] = useState(null);
  const [reworkSourceOriginalAsset, setReworkSourceOriginalAsset] = useState(null);
  const [localUploadHasAlphaMask, setLocalUploadHasAlphaMask] = useState(null);

  const statusErrorCountRef = useRef(0);
  const statusErrorFlagRef = useRef(false);
  const fileInputRef = useRef(null);
  const processingTabUserSelectedRef = useRef(false);
  const previewSectionRef = useRef(null);
  const uploadSequenceRef = useRef(0);
  const location = useLocation();
  const bootstrappedFromInitialFileRef = useRef(false);
  const bootstrappedReworkSessionRef = useRef(null);
  const physicalSizePrefillAppliedRef = useRef(false);

  const { data: parameterData, loading: parametersLoading, error: parametersError } = useTierParameters();
  const hasSourceSelection = Boolean(selectedFile || sessionId);

  const activeStepperIndex = useMemo(() => {
    if (status === 'processing' || status === 'completed' || status === 'failed') {
      return 3;
    }
    if (status === 'ready' && hasSourceSelection) {
      return 2;
    }
    if (hasSourceSelection) {
      return 1;
    }
    return 0;
  }, [hasSourceSelection, status]);

  const normalizePresetValue = useCallback((value) => {
    if (Array.isArray(value)) {
      return JSON.stringify(value);
    }
    if (value === null || value === undefined) {
      return '';
    }
    return String(value);
  }, []);

  const applyPreset = useCallback((presetId) => {
    const allPresets = [
      ...PROCESSING_PRESETS,
      ...userPresets.map((preset) => ({
        id: `user:${preset.id}`,
        label: preset.name,
        description: '',
        overrides: preset.values || {},
      })),
    ];

    const preset = allPresets.find((entry) => entry.id === presetId);
    if (!preset) {
      return;
    }
    setParameterValues((prev) => {
      const next = { ...prev };
      Object.entries(preset.overrides).forEach(([key, value]) => {
        next[key] = value;
      });
      return next;
    });
  }, [userPresets]);

  const handlePresetChange = useCallback((event) => {
    const nextPreset = event.target.value;
    setActivePreset(nextPreset);
    if (nextPreset !== 'custom') {
      applyPreset(nextPreset);
    }
  }, [applyPreset]);

  const handlePresetReset = useCallback(() => {
    if (activePreset === 'custom') {
      return;
    }
    applyPreset(activePreset);
  }, [activePreset, applyPreset]);

  const handlePresetSave = useCallback(async () => {
    if (activePreset && activePreset.startsWith('user:')) {
      const presetId = Number(activePreset.split(':')[1]);
      const existing = userPresets.find((preset) => preset.id === presetId);
      if (!existing) {
        return;
      }
      try {
        setPresetSaving(true);
        setPresetError('');
        const updated = await parameterService.updateUserPreset(presetId, {
          values: parameterValues,
        });
        // Keep local cache in sync so future applications of this preset
        // use the latest saved values.
        setUserPresets((prev) =>
          prev.map((preset) => (preset.id === presetId ? updated : preset))
        );
      } catch (error) {
        setPresetError('Unable to save preset.');
      } finally {
        setPresetSaving(false);
      }
      return;
    }

    const name = window.prompt('Name for new preset');
    if (!name) {
      return;
    }
    try {
      setPresetSaving(true);
      setPresetError('');
      const created = await parameterService.createUserPreset({
        name,
        scope: 'image_processing',
        values: parameterValues,
      });
      setUserPresets((prev) => [created, ...prev]);
      setActivePreset(`user:${created.id}`);
    } catch (error) {
      setPresetError('Unable to save preset.');
    } finally {
      setPresetSaving(false);
    }
  }, [activePreset, parameterValues, userPresets]);

  const handleParameterChange = useCallback((control, value) => {
    setParameterValues((prev) => ({
      ...prev,
      [control.id]: value,
    }));

    if (activePreset !== 'custom' && PRESET_TRACKING_KEYS.has(control.id)) {
      const preset = PROCESSING_PRESETS.find((entry) => entry.id === activePreset);
      const presetValue = preset?.overrides?.[control.id];
      if (presetValue === undefined) {
        setActivePreset('custom');
        return;
      }
      if (normalizePresetValue(presetValue) !== normalizePresetValue(value)) {
        setActivePreset('custom');
      }
    }
  }, [activePreset, normalizePresetValue]);

  useEffect(() => {
    // Load saved presets for this user when the page first mounts.
    let cancelled = false;
    parameterService
      .listUserPresets('image_processing')
      .then((items) => {
        if (!cancelled && Array.isArray(items)) {
          setUserPresets(items);
        }
      })
      .catch(() => {
        // Non-fatal; presets are an enhancement only.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const tierLimitsSummary = parameterData?.raw?.tier_limits || {};
  const uploadResolutionLimit = tierLimitsSummary?.max_upload_resolution;
  const serverParameterConstraints = useMemo(() => {
    const result = {};
    const source = sessionInfo?.constraints;
    if (!source || typeof source !== 'object') {
      return result;
    }

    Object.entries(source).forEach(([stepKey, entries]) => {
      if (!entries || typeof entries !== 'object') {
        return;
      }
      Object.entries(entries).forEach(([paramKey, config]) => {
        if (!config || typeof config !== 'object') {
          return;
        }
        const controlId = `${stepKey}.${paramKey}`;
        result[controlId] = config;
      });
    });
    return result;
  }, [sessionInfo]);

  const uploadHasAlphaMask = Boolean(
    sessionInfo?.original_image?.has_alpha_mask ||
    sessionInfo?.original_image?.has_alpha_channel ||
    sessionInfo?.upload_image_info?.has_alpha_mask ||
    sessionInfo?.upload_image_info?.has_alpha_channel ||
    sessionInfo?.mask_available === true ||
    maskFile ||
    localUploadHasAlphaMask === true
  );
  const backgroundRemovalEnabledFlag = useMemo(
    () => resolveBackgroundRemoval(parameterValues, parameterData),
    [parameterValues, parameterData]
  );

  const clientMaskConstraints = useMemo(() => {
    const alphaProbePending = Boolean(selectedFile) && localUploadHasAlphaMask == null;
    if (uploadHasAlphaMask || backgroundRemovalEnabledFlag !== false || alphaProbePending) {
      return {};
    }

    return {
      'depth_generation.apply_mask': {
        locked: true,
        reason: MASKING_DISABLED_WARNING,
        default: false,
      },
      'depth_generation.save_mask': {
        locked: true,
        reason: MASKING_DISABLED_WARNING,
        default: false,
      },
    };
  }, [uploadHasAlphaMask, backgroundRemovalEnabledFlag, selectedFile, localUploadHasAlphaMask]);

  const effectiveParameterConstraints = useMemo(() => {
    return {
      ...serverParameterConstraints,
      ...clientMaskConstraints,
    };
  }, [serverParameterConstraints, clientMaskConstraints]);

  useEffect(() => {
    if (!effectiveParameterConstraints || Object.keys(effectiveParameterConstraints).length === 0) {
      return;
    }

    setParameterValues((prev) => {
      let changed = false;
      const next = { ...prev };

      Object.entries(effectiveParameterConstraints).forEach(([controlId, constraint]) => {
        if (
          constraint &&
          typeof constraint === 'object' &&
          constraint.locked &&
          Object.prototype.hasOwnProperty.call(constraint, 'default')
        ) {
          if (next[controlId] !== constraint.default) {
            next[controlId] = constraint.default;
            changed = true;
          }
        }
      });

      return changed ? next : prev;
    });
  }, [effectiveParameterConstraints]);

  const preparationConstraintReasons = useMemo(() => {
    const reasons = new Set();
    Object.values(effectiveParameterConstraints).forEach((constraint) => {
      if (constraint && typeof constraint === 'object' && constraint.locked && constraint.reason) {
        reasons.add(constraint.reason);
      }
    });
    return Array.from(reasons);
  }, [effectiveParameterConstraints]);
  const depthResolutionLimit = tierLimitsSummary?.max_depth_resolution;
  const maxUploadFileSizeMb = tierLimitsSummary?.max_file_size_mb
    ?? parameterData?.raw?.limits?.global?.max_file_size_mb
    ?? null;

  const dropzoneHintText = useMemo(() => {
    const segments = [];
    if (maxUploadFileSizeMb) {
      segments.push(`Supports PNG, JPG, GIF up to ${Math.round(maxUploadFileSizeMb)}MB`);
    } else {
      segments.push('Supports PNG, JPG, GIF');
    }

    if (Array.isArray(uploadResolutionLimit) && uploadResolutionLimit.length === 2) {
      segments.push(
        `Images above ${uploadResolutionLimit[0]}×${uploadResolutionLimit[1]} will be downscaled automatically`
      );
    }

    return segments.join('. ');
  }, [maxUploadFileSizeMb, uploadResolutionLimit]);

  const inProcessingFlow = status === 'processing' || status === 'completed' || status === 'failed';

  // Derive step status helpers early for consistent gating of preview/download
  const stepStatuses = sessionInfo?.steps ?? [];
  const hasRunningSteps = stepStatuses.some((step) => step.status === 'pending' || step.status === 'processing');
  const shouldShowStatusPanel = inProcessingFlow && Boolean(sessionInfo);
  const canShowPreviewTab = status === 'completed' && !hasRunningSteps;

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(null);
      return undefined;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedFile) {
      setLocalUploadHasAlphaMask(null);
      return undefined;
    }

    if (maskFile) {
      setLocalUploadHasAlphaMask(true);
      return undefined;
    }

    setLocalUploadHasAlphaMask(null);
    detectAlphaMaskFromFile(selectedFile)
      .then((hasAlpha) => {
        if (!cancelled) {
          setLocalUploadHasAlphaMask(Boolean(hasAlpha));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLocalUploadHasAlphaMask(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [maskFile, selectedFile]);

  const [texturePreviewUrl, setTexturePreviewUrl] = useState(null);

  useEffect(() => {
    if (!textureFile) {
      setTexturePreviewUrl(null);
      return undefined;
    }

    const objectUrl = URL.createObjectURL(textureFile);
    setTexturePreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [textureFile]);

  const prepareFromFile = useCallback(
    async (file) => {
      const isImageMime = typeof file?.type === 'string' && file.type.startsWith('image/');
      const isImageName = /\.(png|jpe?g|gif|webp|bmp|tiff?|heic|heif)$/i.test(file?.name || '');
      if (!isImageMime && !isImageName) {
        setError('Please choose an image file (PNG, JPG, or GIF).');
        setSelectedFile(null);
        setEditorScenePayload(null);
        setResizeInfo(null);
        setStatus('idle');
        setSessionId(null);
        setSessionInfo(null);
        setSessionFiles(null);
        setSessionFilesError('');
        setActiveProcessingTab('status');
        processingTabUserSelectedRef.current = false;
        return;
      }

      setError('');
      setSelectedFile(file);
      setSessionId(null);
      setSessionInfo(null);
      setSessionFiles(null);
      setSessionFilesError('');
      statusErrorCountRef.current = 0;
      statusErrorFlagRef.current = false;
      setActiveProcessingTab('status');
      processingTabUserSelectedRef.current = false;
      setStatus('ready');
      setIsPreparing(false);

      if (Array.isArray(uploadResolutionLimit) && uploadResolutionLimit.length === 2) {
        const [maxWidth, maxHeight] = uploadResolutionLimit;
        const tempUrl = URL.createObjectURL(file);
        const imageProbe = new Image();
        imageProbe.onload = () => {
          if (imageProbe.width > maxWidth || imageProbe.height > maxHeight) {
            const scale = Math.min(maxWidth / imageProbe.width, maxHeight / imageProbe.height);
            const resizedWidth = Math.max(1, Math.round(imageProbe.width * scale));
            const resizedHeight = Math.max(1, Math.round(imageProbe.height * scale));
            setResizeInfo({
              original: [imageProbe.width, imageProbe.height],
              resized: [resizedWidth, resizedHeight],
              limit: [maxWidth, maxHeight],
            });
          } else {
            setResizeInfo(null);
          }
          URL.revokeObjectURL(tempUrl);
        };
        imageProbe.onerror = () => {
          setResizeInfo(null);
          URL.revokeObjectURL(tempUrl);
        };
        imageProbe.src = tempUrl;
      } else {
        setResizeInfo(null);
      }
    },
    [uploadResolutionLimit]
  );

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    prepareFromFile(file);
    setReworkSourceOriginalAsset(null);
    setMaskFile(null);
    setEditorScenePayload(null);
    setTextureFile(null);
    setTextureSettings(null);
  };

  useEffect(() => {
    const initialFile = location.state && location.state.initialFile;
    const initialMaskFile = location.state && location.state.initialMaskFile;
    const initialEditorScene = location.state && location.state.initialEditorScene;
    const initialTexture = location.state && location.state.initialTexture;
    const initialTextureSettings = location.state && location.state.initialTextureSettings;
    const initialPhysicalSizePrefill = location.state && location.state.initialPhysicalSizePrefill;
    if (!initialFile || bootstrappedFromInitialFileRef.current) {
      return;
    }
    bootstrappedFromInitialFileRef.current = true;
    prepareFromFile(initialFile);
    if (initialMaskFile) {
      setMaskFile(initialMaskFile);
    }
    if (initialEditorScene) {
      setEditorScenePayload(initialEditorScene);
    }
    if (initialTexture) {
      setTextureFile(initialTexture);
    }
    if (initialTextureSettings) {
      setTextureSettings(initialTextureSettings);
    }
    if (initialPhysicalSizePrefill && !physicalSizePrefillAppliedRef.current) {
      const widthMm = Number(initialPhysicalSizePrefill.widthMm);
      const depthMm = Number(initialPhysicalSizePrefill.depthMm);
      const zMode = String(initialPhysicalSizePrefill.zMode || 'fixed_mm');
      setParameterValues((prev) => ({
        ...prev,
        ...(Number.isFinite(widthMm) && widthMm > 0 ? { 'mesh_generation.physical_width_mm': widthMm } : {}),
        ...(Number.isFinite(depthMm) && depthMm > 0 ? { 'mesh_generation.physical_depth_mm': depthMm } : {}),
        'mesh_generation.physical_z_mode': zMode,
      }));
      setActivePreset('custom');
      physicalSizePrefillAppliedRef.current = true;
    }
  }, [location.state, prepareFromFile]);

  const reworkBootstrapState = useMemo(() => {
    const state = location.state && typeof location.state === 'object' ? location.state : {};
    return {
      reworkSessionId: state.reworkSessionId || null,
      reworkStepParameters: state.reworkStepParameters,
      reworkTextureSettings: state.reworkTextureSettings,
      reworkAlgorithm: state.reworkAlgorithm,
      reworkMessage: state.reworkMessage,
      sourceSessionRef: state.reworkSourceSessionId,
      reworkOriginalImageFilename: state.reworkOriginalImageFilename,
      reworkMaskAvailable: state.reworkMaskAvailable === true,
    };
  }, [location.state?.reworkSessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const reworkSessionId = reworkBootstrapState.reworkSessionId;
    if (!reworkSessionId || bootstrappedReworkSessionRef.current === reworkSessionId) {
      return;
    }

    bootstrappedReworkSessionRef.current = reworkSessionId;

    const {
      reworkStepParameters,
      reworkTextureSettings,
      reworkAlgorithm,
      reworkMessage,
      sourceSessionRef,
      reworkOriginalImageFilename,
      reworkMaskAvailable,
    } = reworkBootstrapState;

    let cancelled = false;

    const inferImageMimeType = (filename) => {
      const ext = String(filename || '').toLowerCase().split('.').pop();
      if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
      if (ext === 'png') return 'image/png';
      if (ext === 'gif') return 'image/gif';
      if (ext === 'webp') return 'image/webp';
      if (ext === 'bmp') return 'image/bmp';
      if (ext === 'tif' || ext === 'tiff') return 'image/tiff';
      if (ext === 'heic' || ext === 'heif') return 'image/heic';
      return 'image/png';
    };

    const bootstrapRework = async () => {
      setError('');
      setStatus('ready');
      setSessionId(reworkSessionId);
      setReworkSourceSessionId(sourceSessionRef || null);
      setReworkSourceOriginalAsset(
        reworkOriginalImageFilename
          ? {
              sessionId: reworkSessionId,
              stepType: 'background_removal',
              filename: reworkOriginalImageFilename,
            }
          : null
      );
      setSessionInfo(
        reworkMessage || reworkMaskAvailable
          ? {
              ...(reworkMessage ? { message: reworkMessage } : {}),
              ...(reworkMaskAvailable ? { mask_available: true } : {}),
            }
          : null
      );
      setSessionFiles(null);
      setSessionFilesError('');
      setActiveProcessingTab('status');
      processingTabUserSelectedRef.current = false;

      const flattened = flattenStepParametersToValues(reworkStepParameters);
      const hasAlgorithmValue = Object.keys(flattened).some(
        (key) => key.includes('mesh_generation') && key.endsWith('.algorithm')
      );
      if (reworkAlgorithm && !hasAlgorithmValue) {
        flattened['mesh_generation.algorithm'] = reworkAlgorithm;
      }
      if (Object.keys(flattened).length > 0) {
        setParameterValues((prev) => ({ ...prev, ...flattened }));
        setActivePreset('custom');
      }

      if (reworkTextureSettings && typeof reworkTextureSettings === 'object') {
        setTextureSettings(reworkTextureSettings);
      }

      try {
        const sourceSessionIdForOriginal = (
          sourceSessionRef && sourceSessionRef !== reworkSessionId
            ? sourceSessionRef
            : null
        );
        const filesResponse = await pipelineService.listFiles(reworkSessionId);
        if (cancelled) {
          return;
        }
        setSessionFiles(filesResponse);

        let originalDownloadSessionId = reworkSessionId;
        let selectedOriginalRef = pickSourceOriginalFile(filesResponse);

        if (!selectedOriginalRef && sourceSessionIdForOriginal) {
          try {
            const sourceFilesResponse = await pipelineService.listFiles(sourceSessionIdForOriginal);
            const sourceOriginalRef = pickSourceOriginalFile(sourceFilesResponse);
            if (sourceOriginalRef) {
              selectedOriginalRef = sourceOriginalRef;
              originalDownloadSessionId = sourceSessionIdForOriginal;
            }
          } catch {
            // Fall back to the prepared rework session files if source listing fails.
          }
        }

        if (!selectedOriginalRef && reworkOriginalImageFilename) {
          selectedOriginalRef = { stepType: 'background_removal', filename: reworkOriginalImageFilename };
          originalDownloadSessionId = reworkSessionId;
        }

        if (selectedOriginalRef) {
          if (!cancelled) {
            setReworkSourceOriginalAsset({
              sessionId: originalDownloadSessionId,
              stepType: selectedOriginalRef.stepType,
              filename: selectedOriginalRef.filename,
              file: selectedOriginalRef.file || null,
            });
          }
          const originalBlob = await pipelineService.downloadFile(
            originalDownloadSessionId,
            selectedOriginalRef.stepType,
            selectedOriginalRef.file || selectedOriginalRef.filename
          );
          if (!cancelled) {
            const sourceName = String(selectedOriginalRef.filename).split('/').pop() || 'rework-source.png';
            const sourceType = originalBlob.type || inferImageMimeType(sourceName);
            const sourceFile = new File([originalBlob], sourceName, { type: sourceType });
            await prepareFromFile(sourceFile);
            if (!cancelled) {
              setSessionId(reworkSessionId);
              setReworkSourceSessionId(sourceSessionRef || null);
              setStatus('ready');
            }
          }
        }

        const textureRef = pickSourceTextureFile(filesResponse);
        if (textureRef) {
          const textureBlob = await pipelineService.downloadFile(
            reworkSessionId,
            textureRef.stepType,
            textureRef.file || textureRef.filename
          );
          if (!cancelled) {
            const textureName = String(textureRef.filename).split('/').pop() || 'rework-texture.png';
            const textureType = textureBlob.type || inferImageMimeType(textureName);
            const sessionTextureFile = new File([textureBlob], textureName, { type: textureType });
            setTextureFile(sessionTextureFile);
          }
        }
      } catch (bootstrapError) {
        console.warn('Unable to fully bootstrap rework source assets.', bootstrapError);
        // Best-effort direct fallback: rework response includes the cloned
        // original filename, so we can fetch it without depending on file
        // listing shape/availability.
        if (!cancelled && reworkOriginalImageFilename) {
          if (!cancelled) {
            setReworkSourceOriginalAsset({
              sessionId: reworkSessionId,
              stepType: 'background_removal',
              filename: reworkOriginalImageFilename,
            });
          }
          try {
            const fallbackBlob = await pipelineService.downloadFile(
              reworkSessionId,
              'background_removal',
              reworkOriginalImageFilename
            );
            const fallbackName = String(reworkOriginalImageFilename).split('/').pop() || 'rework-source.png';
            const fallbackType = fallbackBlob.type || inferImageMimeType(fallbackName);
            const fallbackFile = new File([fallbackBlob], fallbackName, { type: fallbackType });
            await prepareFromFile(fallbackFile);
            if (!cancelled) {
              setSessionId(reworkSessionId);
              setReworkSourceSessionId(sourceSessionRef || null);
              setStatus('ready');
            }
          } catch (fallbackError) {
            console.warn('Rework source direct fallback also failed.', fallbackError);
          }
        }
        if (!cancelled) {
          setSessionFilesError('');
          setSessionId(reworkSessionId);
          setStatus('ready');
        }
      }
    };

    bootstrapRework();

    return () => {
      cancelled = true;
    };
  }, [reworkBootstrapState, prepareFromFile]);

  const handleTriggerSelect = () => {
    fileInputRef.current?.click();
  };

  const buildSceneFile = useCallback(() => {
    if (!editorScenePayload) {
      return null;
    }
    try {
      const payload = JSON.stringify(editorScenePayload, null, 2);
      return new File([payload], 'scene.json', { type: 'application/json' });
    } catch (_error) {
      return null;
    }
  }, [editorScenePayload]);

  const resetSelection = () => {
    uploadSequenceRef.current += 1;
    setSelectedFile(null);
    setStatus('idle');
    setError('');
    setSessionId(null);
    setSessionInfo(null);
    setSessionFiles(null);
    setSessionFilesError('');
    setResizeInfo(null);
    setIsPreparing(false);
    setReworkSourceSessionId(null);
    setReworkSourceOriginalAsset(null);
    setEditorScenePayload(null);
    setActivePreviewTab('mesh');
    setActiveProcessingTab('status');
    processingTabUserSelectedRef.current = false;
    statusErrorCountRef.current = 0;
    statusErrorFlagRef.current = false;
    setTextureFile(null);
    setTextureSettings(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleStartNewJob = () => {
    // New jobs should begin from the image tool selection pane.
    resetSelection();
    navigate('/image-tools');
  };

  const handleReworkCurrentSession = async () => {
    if (!sessionId) {
      return;
    }
    try {
      setError('');
      const response = await pipelineService.reworkSession(sessionId);
      navigate('/image-processing', {
        state: {
          reworkSessionId: response.session_id,
          reworkSourceSessionId: response.source_session_id || sessionId,
          reworkAlgorithm: response.algorithm || null,
          reworkStepParameters: response.step_parameters || {},
          reworkTextureSettings: response.texture_settings || null,
          reworkOriginalImageFilename: response.original_image?.filename || null,
          reworkMessage: response.message || '',
          reworkMaskAvailable: response.mask_available === true,
        },
      });
    } catch (apiError) {
      const detail = apiError?.response?.data?.detail;
      const fallbackMessage = typeof detail === 'string'
        ? detail
        : 'Unable to prepare rework session. Please try again.';
      setError(fallbackMessage);
    }
  };

  useEffect(() => {
    if (!parameterData?.groups) {
      return;
    }

    setParameterValues((prev) => {
      const next = { ...prev };
      parameterData.groups.forEach((group) => {
        group.controls.forEach((control) => {
          if (next[control.id] === undefined) {
            if (control.defaultValue !== undefined) {
              next[control.id] = control.defaultValue;
            } else {
              next[control.id] = '';
            }
          }
        });
      });
      return next;
    });
  }, [parameterData]);

  const handleStartProcessing = async () => {
    if (!selectedFile && !sessionId) {
      setError('Select an image before processing.');
      return;
    }

    if (isPreparing) {
      setError('Analyzing upload. Please wait a moment.');
      return;
    }

    const algorithm = resolveAlgorithm(parameterValues, parameterData?.groups);
    if (!algorithm) {
      setError('Select a mesh generation algorithm to continue.');
      return;
    }

    const backgroundRemovalEnabled = resolveBackgroundRemoval(parameterValues, parameterData);
    const stepParameters = buildStepParameters(parameterValues, parameterData);
    const preparedStepParameters = Object.keys(stepParameters).length ? stepParameters : null;

    setError('');
    setStatus('processing');
    setSessionFiles(null);
    setSessionFilesError('');
    setResizeInfo(null);
    setActivePreviewTab('mesh');
    setActiveProcessingTab('status');
    processingTabUserSelectedRef.current = false;
    statusErrorCountRef.current = 0;
    statusErrorFlagRef.current = false;
    const hadExistingSession = Boolean(sessionId);

    try {
      const startOptions = {
        algorithm,
        backgroundRemoval: backgroundRemovalEnabled,
        stepParameters: preparedStepParameters,
      };
      const sceneFile = buildSceneFile();

      let response;
      if (sessionId) {
        try {
          response = await pipelineService.startSession(sessionId, startOptions);
        } catch (startError) {
          if (startError?.response?.status === 404 && selectedFile) {
            response = await pipelineService.createSession({
              file: selectedFile,
              maskFile,
              sceneFile,
              textureFile,
              textureSettings,
              ...startOptions,
            });
          } else {
            throw startError;
          }
        }
      } else {
        response = await pipelineService.createSession({
          file: selectedFile,
          maskFile,
          sceneFile,
          textureFile,
          textureSettings,
          ...startOptions,
        });
      }

      setSessionId(response.session_id);
      setSessionInfo(response);
    } catch (apiError) {
      const detail = apiError?.response?.data?.detail;
      const fallbackMessage = typeof detail === 'string' ? detail : 'Failed to start processing pipeline.';
      setError(fallbackMessage);
      setStatus(hadExistingSession || selectedFile ? 'ready' : 'idle');
      if (!hadExistingSession) {
        setSessionId(null);
        setSessionInfo(null);
      }
    }
  };

  const handleProcessingTabChange = (tabId) => {
    processingTabUserSelectedRef.current = true;
    setActiveProcessingTab(tabId);
  };

  const buildSessionFileUrl = useCallback(
    (assetSessionId, stepType, fileOrFilename) => {
      if (!assetSessionId || !stepType || !fileOrFilename) {
        return null;
      }
      return pipelineService.resolveFilePreviewUrl(assetSessionId, stepType, fileOrFilename, {
        useFallback: true,
      });
    },
    []
  );

  const buildFileUrl = useCallback(
    (stepType, fileOrFilename) => buildSessionFileUrl(sessionId, stepType, fileOrFilename),
    [buildSessionFileUrl, sessionId]
  );

  const sourceOriginalAsset = useMemo(() => {
    if (reworkSourceOriginalAsset?.filename) {
      return reworkSourceOriginalAsset;
    }
    if (!sessionId) {
      return null;
    }
    const fallback = pickSourceOriginalFile(sessionFiles);
    return fallback ? { sessionId, ...fallback } : null;
  }, [reworkSourceOriginalAsset, sessionId, sessionFiles]);
  const sourceTextureAsset = useMemo(
    () => (sessionId ? pickSourceTextureFile(sessionFiles) : null),
    [sessionId, sessionFiles]
  );
  const sourceOriginalPreviewUrl = useMemo(() => {
    if (selectedFile || !sourceOriginalAsset) {
      return null;
    }
    return buildSessionFileUrl(
      sourceOriginalAsset.sessionId || sessionId,
      sourceOriginalAsset.stepType,
      sourceOriginalAsset.file || sourceOriginalAsset.filename
    );
  }, [buildSessionFileUrl, selectedFile, sessionId, sourceOriginalAsset]);
  const sourceTexturePreviewUrl = useMemo(() => {
    if (textureFile || !sourceTextureAsset) {
      return null;
    }
    return buildFileUrl(sourceTextureAsset.stepType, sourceTextureAsset.file || sourceTextureAsset.filename);
  }, [buildFileUrl, textureFile, sourceTextureAsset]);

  const sourceOriginalFilename = sourceOriginalAsset?.filename
    ? String(sourceOriginalAsset.filename).split('/').pop()
    : null;
  const sourceTextureFilename = sourceTextureAsset?.filename
    ? String(sourceTextureAsset.filename).split('/').pop()
    : null;

  useEffect(() => {
    if (!sessionId || status !== 'processing') {
      return undefined;
    }

    let isCancelled = false;
    let pollTimerId = null;
    let pollInFlight = false;

    const scheduleNextPoll = () => {
      if (isCancelled) {
        return;
      }
      const delay = getVisibilityAwareDelay(STATUS_POLL_VISIBLE_INTERVAL_MS, STATUS_POLL_HIDDEN_INTERVAL_MS);
      pollTimerId = window.setTimeout(() => {
        pollStatus();
      }, delay);
    };

    const pollStatus = async () => {
      if (isCancelled || pollInFlight) {
        return;
      }
      pollInFlight = true;
      let shouldContinuePolling = true;

      try {
        const statusData = await pipelineService.getStatus(sessionId);
        if (isCancelled) {
          return;
        }

        statusErrorCountRef.current = 0;
        if (statusErrorFlagRef.current) {
          statusErrorFlagRef.current = false;
          setError((prev) => (prev === STATUS_POLL_ERROR_MESSAGE ? '' : prev));
        }

        setSessionInfo(statusData);

        if (statusData.status === 'completed') {
          setStatus('completed');
          setError('');
          shouldContinuePolling = false;
          return;
        }

        if (statusData.status === 'failed' || statusData.status === 'cancelled') {
          const message = statusData.error_message || 'Processing did not complete successfully.';
          setError(message);
          setStatus('failed');
          shouldContinuePolling = false;
        }
      } catch (pollError) {
        if (isCancelled) {
          return;
        }
        statusErrorCountRef.current += 1;
        // Log error internally without console output
        if (statusErrorCountRef.current >= 2) {
          statusErrorFlagRef.current = true;
          setError((prev) => (prev && prev !== STATUS_POLL_ERROR_MESSAGE ? prev : STATUS_POLL_ERROR_MESSAGE));
        }
      } finally {
        pollInFlight = false;
        if (shouldContinuePolling) {
          scheduleNextPoll();
        }
      }
    };

    const handleVisibilityChange = () => {
      if (isCancelled || document.visibilityState !== 'visible') {
        return;
      }
      if (pollTimerId) {
        window.clearTimeout(pollTimerId);
        pollTimerId = null;
      }
      pollStatus();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    pollStatus();

    return () => {
      isCancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (pollTimerId) {
        window.clearTimeout(pollTimerId);
      }
    };
  }, [sessionId, status]);

  useEffect(() => {
    if (status !== 'completed' || !sessionId) {
      return undefined;
    }

    let isCancelled = false;
    let retryTimerId = null;
    let attempts = 0;
    const maxAttempts = 10; // Increased for long-running processes

    const refreshStatus = () => {
      attempts += 1;
      pipelineService
        .getStatus(sessionId)
        .then((data) => {
          if (isCancelled) {
            return;
          }
          setSessionInfo(data);
          const hasRunningSteps = data.steps?.some((step) => step.status === 'pending' || step.status === 'processing');
          if (hasRunningSteps) {
            attempts = 0; // Reset counter when there are active steps
            retryTimerId = window.setTimeout(
              refreshStatus,
              getVisibilityAwareDelay(1200, STATUS_POLL_RESUME_MIN_DELAY_MS)
            );
          } else if (attempts < maxAttempts) {
            // Continue polling for a few more attempts even without running steps
            retryTimerId = window.setTimeout(
              refreshStatus,
              getVisibilityAwareDelay(2000, STATUS_POLL_RESUME_MIN_DELAY_MS)
            );
          }
        })
        .catch(() => {
          if (!isCancelled && attempts < maxAttempts) {
            retryTimerId = window.setTimeout(
              refreshStatus,
              getVisibilityAwareDelay(1500, STATUS_POLL_RESUME_MIN_DELAY_MS)
            );
          }
        });
    };

    const initialTimerId = window.setTimeout(
      refreshStatus,
      getVisibilityAwareDelay(400, STATUS_POLL_RESUME_MIN_DELAY_MS)
    );

    return () => {
      isCancelled = true;
      window.clearTimeout(initialTimerId);
      if (retryTimerId) {
        window.clearTimeout(retryTimerId);
      }
    };
  }, [status, sessionId]);

  useEffect(() => {
    if (status !== 'completed' || !sessionId) {
      return undefined;
    }

    let isCancelled = false;
    let retryTimerId = null;
    let attempt = 0;
    const maxAttempts = 3;

    const fetchFiles = () => {
      if (isCancelled) {
        return;
      }
      attempt += 1;
      setIsFetchingFiles(true);
      setSessionFilesError('');

      pipelineService
        .listFiles(sessionId)
        .then((response) => {
          if (isCancelled) {
            return;
          }
          setSessionFiles(response);
          setSessionFilesError('');
        })
        .catch((listError) => {
          if (isCancelled) {
            return;
          }
          const detail = listError?.response?.data?.detail;
          const message = typeof detail === 'string' ? detail : 'Unable to load pipeline files.';
          if (attempt < maxAttempts) {
            retryTimerId = window.setTimeout(fetchFiles, 2000);
          } else {
            setSessionFiles(null);
            setSessionFilesError(message);
          }
        })
        .finally(() => {
          if (!isCancelled) {
            setIsFetchingFiles(false);
          }
        });
    };

    fetchFiles();

    return () => {
      isCancelled = true;
      if (retryTimerId) {
        window.clearTimeout(retryTimerId);
      }
    };
  }, [sessionId, status]);

  useEffect(() => {
    if (status === 'processing') {
      if (activeProcessingTab !== 'status') {
        setActiveProcessingTab('status');
      }
      processingTabUserSelectedRef.current = false;
      if (activePreviewTab !== 'mesh') {
        setActivePreviewTab('mesh');
      }
    }

    // Switch to download only when pipeline reports completed AND no steps are still running
    if (status === 'completed' && !processingTabUserSelectedRef.current && activeProcessingTab !== 'download') {
      const stillRunning = hasRunningSteps;
      if (!stillRunning) {
        setActiveProcessingTab('download');
      }
    }

    if ((status === 'failed' || status === 'idle' || status === 'ready') && activeProcessingTab !== 'status') {
      setActiveProcessingTab('status');
      processingTabUserSelectedRef.current = false;
    }

    if (status !== 'completed' && activePreviewTab !== 'mesh') {
      setActivePreviewTab('mesh');
    }
  }, [status, activeProcessingTab, activePreviewTab, hasRunningSteps]);

  useEffect(() => {
    const noRunningSteps = Array.isArray(sessionInfo?.steps)
      ? !sessionInfo.steps.some((step) => step.status === 'pending' || step.status === 'processing')
      : false;
    if (
      status === 'completed' &&
      noRunningSteps &&
      (activeProcessingTab === 'preview' || activeProcessingTab === 'download') &&
      previewSectionRef.current
    ) {
      const element = previewSectionRef.current;
      const offset = 72; // Keep consistent with processing top padding
      const top = element.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, [status, sessionInfo, activeProcessingTab]);

  const meshAssets = useMemo(() => {
    debugLog('Computing meshAssets...', { sessionFiles, sessionInfo });
    
    const filesEntry = sessionFiles?.files_by_step
      ? Object.values(sessionFiles.files_by_step).find((entry) => entry.step_type === 'mesh_generation')
      : null;
    debugLog('Files entry from sessionFiles:', filesEntry);

    const stepInfo = sessionInfo?.steps?.find((step) => step.step_type === 'mesh_generation');
    debugLog('Step info from sessionInfo:', stepInfo);
    
    const mergedFiles = dedupeFiles(mergeFileEntries(filesEntry), stepInfo?.files);
    debugLog('Merged files:', mergedFiles);
    
    const stepType = filesEntry?.step_type || stepInfo?.step_type || 'mesh_generation';
    debugLog('Step type:', stepType);

    const result = {
      stepType,
      files: mergedFiles,
    };
    debugLog('Final meshAssets result:', result);
    
    return result;
  }, [sessionFiles, sessionInfo]);

  const backgroundAssets = useMemo(() => {
    const filesEntry = sessionFiles?.files_by_step
      ? Object.values(sessionFiles.files_by_step).find((entry) => entry.step_type === 'background_removal')
      : null;
    const stepInfo = sessionInfo?.steps?.find((step) => step.step_type === 'background_removal');
    const mergedFiles = dedupeFiles(mergeFileEntries(filesEntry), stepInfo?.files);
    const stepType = filesEntry?.step_type || stepInfo?.step_type || 'background_removal';

    return {
      stepType,
      files: mergedFiles,
    };
  }, [sessionFiles, sessionInfo]);

  const meshObjUrl = useMemo(() => {
    debugLog('Computing meshObjUrl...', { meshAssets });
    const objFile =
      meshAssets.files.find((file) => /mesh_preview\.obj$/i.test(file.filename)) ||
      meshAssets.files.find((file) => isObjFile(file.filename));
    debugLog('Found OBJ file:', objFile);
    if (!objFile) {
      debugLog('No OBJ file found in meshAssets.files');
      return null;
    }
    const url = buildFileUrl(meshAssets.stepType, objFile);
    debugLog('Generated mesh OBJ URL:', url);
    return url;
  }, [buildFileUrl, meshAssets]);

  const meshFileSize = useMemo(() => {
    const objFile = meshAssets.files.find((file) => isObjFile(file.filename));
    debugLog('Mesh file object:', objFile);
    debugLog('Available file properties:', objFile ? Object.keys(objFile) : 'No file found');
    return objFile?.size || objFile?.file_size || 0;
  }, [meshAssets]);

  const meshTextureUrl = useMemo(() => {
    debugLog('Computing meshTextureUrl...', { meshAssets });
    const textureFile = meshAssets.files.find((file) => isTextureFile(file.filename));
    debugLog('Found texture file:', textureFile);
    if (!textureFile) {
      debugLog('No texture file found in meshAssets.files');
      return null;
    }
    const url = buildFileUrl(meshAssets.stepType, textureFile);
    debugLog('Generated mesh texture URL:', url);
    return url;
  }, [buildFileUrl, meshAssets]);

  const originalImageAssetUrl = useMemo(() => {
    if (!canShowPreviewTab) {
      return null;
    }

    const prioritized = backgroundAssets.files.find((file) =>
      isImageFile(file.filename) && file.filename.toLowerCase().includes('original')
    );
    const fallbackImage = prioritized || backgroundAssets.files.find((file) => isImageFile(file.filename));
    return fallbackImage ? buildFileUrl(backgroundAssets.stepType, fallbackImage) : null;
  }, [backgroundAssets, buildFileUrl, canShowPreviewTab]);

  const previewTabs = useMemo(() => {
    if (!canShowPreviewTab) {
      return [];
    }

    const tabs = [
      {
        id: 'mesh',
        label: '3D Mesh',
        available: Boolean(meshObjUrl),
        content: (
          <div>
            {meshFileSize > 10 * 1024 * 1024 && (
              <div className="result-preview__warning">
                <p>⚠️ Large mesh file ({(meshFileSize / (1024 * 1024)).toFixed(1)}MB) - optimizing for browser display...</p>
              </div>
            )}
            <MeshPreview objUrl={meshObjUrl} textureUrl={meshTextureUrl} />
          </div>
        ),
      },
      {
        id: 'original',
        label: 'Original Image',
        available: Boolean(originalImageAssetUrl || previewUrl),
        content: (
          <SecureImage
            src={originalImageAssetUrl}
            fallbackSrc={previewUrl}
            alt="Original upload"
            className="result-tab__image"
            loadingFallback={<p className="result-preview__meta">Loading image…</p>}
          />
        ),
      },
      {
        id: 'texture',
        label: 'Texture',
        available: Boolean(meshTextureUrl),
        content: (
          <SecureImage
            src={meshTextureUrl}
            alt="Mesh texture"
            className="result-tab__image"
            loadingFallback={<p className="result-preview__meta">Loading texture…</p>}
          />
        ),
      },
    ].filter((tab) => tab.available);

    return tabs;
  }, [canShowPreviewTab, meshObjUrl, meshTextureUrl, originalImageAssetUrl, previewUrl, meshFileSize]);

  useEffect(() => {
    if (!previewTabs.length) {
      return;
    }
    if (!previewTabs.some((tab) => tab.id === activePreviewTab)) {
      setActivePreviewTab(previewTabs[0].id);
    }
  }, [previewTabs, activePreviewTab]);

  // moved earlier for use in memoized values

  const renderPipelineStatus = () => {
    if (!shouldShowStatusPanel) {
      return null;
    }

    const visibleSteps = stepStatuses.filter((step) => step.status !== 'skipped');

    return (
      <div className="pipeline-status">
        <div className="pipeline-status__header">
          <span className="pipeline-status__badge">Pipeline Status</span>
          <span className={`pipeline-status__state pipeline-status__state--${sessionInfo.status}`}>
            {prettifyStepName(sessionInfo.status)}
          </span>
        </div>
        <div className="pipeline-status__progress">
          <div className="pipeline-status__progress-bar">
            <div
              className="pipeline-status__progress-bar-fill"
              style={{ width: `${sessionInfo.progress_percentage ?? 0}%` }}
            />
          </div>
          <div className="pipeline-status__progress-meta">
            <span>
              {sessionInfo.completed_steps} / {sessionInfo.total_steps} steps complete
            </span>
            {sessionInfo.current_step ? (
              <span>Current step: {prettifyStepName(sessionInfo.current_step)}</span>
            ) : null}
          </div>
        </div>
        {visibleSteps.length > 0 ? (
          <ul className="pipeline-status__steps">
            {visibleSteps.map((step) => (
              <li key={step.step_order} className={`pipeline-status__step pipeline-status__step--${step.status}`}>
                <span className="pipeline-status__step-name">{prettifyStepName(step.step_type)}</span>
                <span className="pipeline-status__step-status">{prettifyStepName(step.status)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="pipeline-status__empty">Waiting for step updates…</p>
        )}
      </div>
    );
  };

  const renderResultTabs = () => {
    if (!canShowPreviewTab) {
      return null;
    }

    if (!previewTabs.length) {
      return (
        <div className="result-preview" ref={previewSectionRef}>
          <div className="result-preview__header">
            <h2>Result Preview</h2>
          </div>
          <p className="result-preview__error">No preview assets are available for this run.</p>
        </div>
      );
    }

    const activeTab = previewTabs.find((tab) => tab.id === activePreviewTab) || previewTabs[0];

    return (
      <div className="result-preview" ref={previewSectionRef}>
        <div className="result-preview__header">
          <h2>Result Preview</h2>
          {isFetchingFiles && <span className="result-preview__meta">Loading outputs…</span>}
          {sessionFilesError && <span className="result-preview__error">{sessionFilesError}</span>}
        </div>
        <div className="result-preview__tabs">
          {previewTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`result-preview__tab ${tab.id === activePreviewTab ? 'result-preview__tab--active' : ''}`}
              onClick={() => setActivePreviewTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="result-preview__panel">
          <div className="result-preview__panel-inner">{activeTab?.content}</div>
        </div>
      </div>
    );
  };

  const canReworkCurrentSession = Boolean(sessionId) && (status === 'completed' || status === 'failed');

  return (
    <section className={`image-processing ${inProcessingFlow ? 'image-processing--processing' : ''}`}>
      <header className="image-processing__header">
        <h1>Image Processing</h1>
      </header>

      <div className="processing-stepper" aria-label="Pipeline steps">
        {PROCESSING_STEPS.map((step, index) => {
          const itemClass = index <= activeStepperIndex ? 'processing-stepper__item processing-stepper__item--active' : 'processing-stepper__item';
          return (
            <div key={step.id} className={itemClass}>
              <span className="processing-stepper__counter">{index + 1}</span>
              <div>
                <p className="processing-stepper__title">{step.title}</p>
                <p className="processing-stepper__description">{step.description}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className={`image-processing__card${inProcessingFlow ? ' image-processing__card--processing' : ''}`}>
        {!inProcessingFlow ? (
          <div className={`image-processing__layout ${hasSourceSelection ? 'image-processing__layout--dual' : 'image-processing__layout--single'}`}>
            <div className="image-processing__main">
              <div className="image-processing__section-card">
                <div className="image-processing__section-header">
                  <span className="image-processing__section-label">Step 1</span>
                  <div>
                    <h3>Upload Image</h3>
                    <p>Drag and drop or browse from your device.</p>
                  </div>
                </div>
                <div className="image-processing__section-body">
                  <div className="image-upload">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleFileChange}
                    />

                    <div
                      className={`upload-stage ${hasSourceSelection ? 'upload-stage--preview' : ''}`}
                      role={!hasSourceSelection ? 'button' : undefined}
                      tabIndex={!hasSourceSelection ? 0 : undefined}
                      onClick={!hasSourceSelection ? handleTriggerSelect : undefined}
                    >
                      {!hasSourceSelection ? (
                        <div className="upload-dropzone">
                          <div className="dropzone-icon" aria-hidden="true">
                            <svg
                              width="36"
                              height="36"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.6"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="17 8 12 3 7 8" />
                              <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                          </div>
                          <div className="dropzone-copy">
                            <p className="dropzone-title">Drop your image here or click to browse</p>
                            <p className="dropzone-hint">{dropzoneHintText || 'Supports PNG, JPG, GIF up to 20MB'}</p>
                            {Array.isArray(depthResolutionLimit) && depthResolutionLimit.length === 2 ? (
                              <p className="dropzone-hint dropzone-hint--secondary">
                                Depth processing capped at {depthResolutionLimit[0]}×{depthResolutionLimit[1]}.
                              </p>
                            ) : null}
                          </div>
                        </div>
                      ) : (
                        <div className="preview-canvas">
                          {previewUrl ? <img src={previewUrl} alt="Selected preview" /> : null}
                          {!previewUrl && sourceOriginalPreviewUrl ? (
                            <SecureImage
                              src={sourceOriginalPreviewUrl}
                              alt="Rework source preview"
                              className="result-tab__image"
                              loadingFallback={<p className="result-preview__meta">Loading source preview…</p>}
                            />
                          ) : null}
                          {!previewUrl && !sourceOriginalPreviewUrl ? (
                            <p className="result-preview__meta">Using source image from the prepared rework session.</p>
                          ) : null}
                        </div>
                      )}
                    </div>

                    {hasSourceSelection ? (
                      <div className="upload-preview">
                        <div className="preview-meta">
                          <p className="preview-name">{selectedFile?.name || sourceOriginalFilename || 'Prepared source image'}</p>
                          <p className="preview-size">
                            {selectedFile ? `${(selectedFile.size / 1024).toFixed(1)} KB` : 'Loaded from previous session'}
                          </p>
                          {reworkSourceSessionId ? (
                            <p className="result-preview__meta">Rework source: {reworkSourceSessionId}</p>
                          ) : null}
                          <div className="preview-actions">
                            <button type="button" className="link-button" onClick={handleTriggerSelect}>
                              Replace image
                            </button>
                            <button type="button" className="link-button" onClick={resetSelection}>
                              Remove
                            </button>
                          </div>
                        </div>
                        {textureFile || sourceTexturePreviewUrl ? (
                          <div className="texture-preview">
                            {texturePreviewUrl ? (
                              <div className="texture-preview__thumb">
                                <img src={texturePreviewUrl} alt="Texture preview from editor" />
                              </div>
                            ) : null}
                            {!texturePreviewUrl && sourceTexturePreviewUrl ? (
                              <div className="texture-preview__thumb">
                                <SecureImage
                                  src={sourceTexturePreviewUrl}
                                  alt={sourceTextureFilename || 'Texture preview'}
                                  className="result-tab__image"
                                  loadingFallback={<p className="result-preview__meta">Loading texture preview…</p>}
                                />
                              </div>
                            ) : null}
                            <p className="texture-preview__label">
                              The 3D OBJ/MTL texture will use the edited image from the Texture &amp; Tone step.
                            </p>
                          </div>
                        ) : null}
                      </div>
                    ) : null}

                    {!hasSourceSelection && error ? (
                      <div className="info-banner info-banner--error">{error}</div>
                    ) : null}
                  </div>
                </div>
              </div>

              {hasSourceSelection ? (
                <div className="image-processing__section-card">
                  <div className="image-processing__section-header">
                    <span className="image-processing__section-label">Step 2</span>
                    <div>
                      <h3>Review &amp; Prepare</h3>
                      <p>Review upload info and start the job.</p>
                    </div>
                  </div>
                  <div className="image-processing__section-body">
                    {resizeInfo ? (
                      <div className="info-banner info-banner--neutral" role="status">
                        <p>
                          We will downscale {resizeInfo.original[0]}×{resizeInfo.original[1]} to {resizeInfo.resized[0]}×{resizeInfo.resized[1]}
                          {' '}to fit your tier limit ({resizeInfo.limit[0]}×{resizeInfo.limit[1]}).
                        </p>
                      </div>
                    ) : null}

                    {/* We no longer perform a separate server-side upload analysis step.
                        Any upload-related limits (resolution, file size) are enforced
                        when the job actually starts, so we don't show pre-flight
                        alpha-channel / mask warnings here anymore. */}

                    {preparationConstraintReasons.length > 0 ? (
                      <div className="info-banner info-banner--warning">
                        <p>Locked parameters:</p>
                        <ul>
                          {preparationConstraintReasons.map((reason) => (
                            <li key={reason}>{reason}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}

                    {hasSourceSelection && error ? (
                      <div className="info-banner info-banner--error">{error}</div>
                    ) : null}

                    <div className="upload-actions">
                      <button
                        type="button"
                        className="btn-primary upload-submit"
                        onClick={handleStartProcessing}
                        disabled={status === 'processing' || (!selectedFile && !sessionId) || isPreparing}
                      >
                        {status === 'processing' ? 'Processing…' : isPreparing ? 'Analyzing…' : 'Start Processing'}
                      </button>
                      <button type="button" className="btn-secondary" onClick={resetSelection} disabled={!hasSourceSelection}>
                        Reset
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
            
            {hasSourceSelection && (
              <div className="image-processing__sidebar">
                <div className="image-processing__sidebar-inner">
                  <div className="image-processing__section-card image-processing__section-card--sidebar">
                    <div className="image-processing__section-header">
                      <span className="image-processing__section-label">Step 3</span>
                      <div>
                        <h3>Processing Parameters</h3>
                        <p>Pick a preset or fine-tune advanced options.</p>
                      </div>
                    </div>
                    <div className="image-processing__section-body">
                      {parametersLoading && <p>Loading tier parameters…</p>}
                      {parametersError && <p className="parameter-error">Unable to load parameters.</p>}

                      {!parametersLoading && !parametersError && parameterData?.groups?.length > 0 ? (
                        <>
                          <div className="processing-preset-bar">
                            <label htmlFor="processing-preset-select">Preset</label>
                            <div className="processing-preset-bar__controls">
                              <select
                                id="processing-preset-select"
                                value={activePreset}
                                onChange={handlePresetChange}
                              >
                                {PROCESSING_PRESETS.map((preset) => (
                                  <option key={preset.id} value={preset.id}>
                                    {preset.label}
                                  </option>
                                ))}
                                {userPresets.map((preset) => (
                                  <option key={`user:${preset.id}`} value={`user:${preset.id}`}>
                                    {preset.name}
                                  </option>
                                ))}
                                <option value="custom">Custom</option>
                              </select>
                              <button
                                type="button"
                                className="btn-tertiary"
                                onClick={handlePresetReset}
                                disabled={activePreset === 'custom'}
                              >
                                Reset to preset
                              </button>
                              <button
                                type="button"
                                className="btn-tertiary"
                                onClick={handlePresetSave}
                                disabled={presetSaving}
                              >
                                {activePreset && activePreset.startsWith('user:') ? 'Save preset' : 'Save as preset'}
                              </button>
                            </div>
                            {presetError && <p className="parameter-error">{presetError}</p>}
                          </div>

                          <div className="parameter-shell parameter-shell--sidebar">
                            <ParameterPanel
                              groups={parameterData.groups}
                              values={parameterValues}
                              onChange={handleParameterChange}
                              className="parameter-panel--sidebar"
                              constraints={effectiveParameterConstraints}
                            />
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="processing-view">
            {error && (
              <div className={status === 'failed' ? 'upload-error upload-error--prominent' : 'upload-error'}>{error}</div>
            )}

            {sessionId && (
              <div className="processing-view__session">
                <p className="processing-view__session-id">
                  Session ID: <strong>{sessionId}</strong>
                </p>
                {sessionInfo?.message ? <p className="processing-view__session-message">{sessionInfo.message}</p> : null}
              </div>
            )}

            <div className="processing-tabs">
              <button
                type="button"
                className={`processing-tab ${activeProcessingTab === 'status' ? 'processing-tab--active' : ''}`}
                onClick={() => handleProcessingTabChange('status')}
              >
                Pipeline Status
              </button>
              <button
                type="button"
                className={`processing-tab ${
                  activeProcessingTab === 'preview' ? 'processing-tab--active' : ''
                } ${!canShowPreviewTab ? 'processing-tab--disabled' : ''}`}
                onClick={() => canShowPreviewTab && handleProcessingTabChange('preview')}
                disabled={!canShowPreviewTab}
              >
                Result Preview
              </button>
              <button
                type="button"
                className={`processing-tab ${
                  activeProcessingTab === 'download' ? 'processing-tab--active' : ''
                } ${!canShowPreviewTab ? 'processing-tab--disabled' : ''}`}
                onClick={() => canShowPreviewTab && handleProcessingTabChange('download')}
                disabled={!canShowPreviewTab}
              >
                Download Files
              </button>
            </div>

            <div className="processing-section">
              {(activeProcessingTab === 'status' || !canShowPreviewTab) && renderPipelineStatus()}
              {canShowPreviewTab && activeProcessingTab === 'preview' ? renderResultTabs() : null}
              {canShowPreviewTab && activeProcessingTab === 'download' ? (
                <DownloadSection 
                  sessionId={sessionId}
                  sessionFiles={sessionFiles}
                  sessionInfo={sessionInfo}
                  loading={isFetchingFiles}
                />
              ) : null}
            </div>

            <div className="processing-view__actions">
              {canReworkCurrentSession ? (
                <button type="button" className="btn-primary" onClick={handleReworkCurrentSession}>
                  Rework This Job
                </button>
              ) : null}
              <button type="button" className="btn-secondary" onClick={handleStartNewJob}>
                Start New Job
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ImageProcessingPage;
